import { useCallback, useRef, useState } from "react";

import moment from "moment";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    JiraFFLContentGenerationResponse,
    MadlibContentGenerationChildWorkItem,
    WizardCreateProjectSampleIssue,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { useCloudStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { fallbackCgsData } from "@/templates/paid-landing/utils/paid-jira-ai-ffl.ts";

import { confiugredIssueTypes } from "../views/setup/jira/constants";
import { Transition, Priority } from "../views/setup/jira/utils";

const PROJECT_CREATE_TASK_POLLING_TIMEOUT = 25000;
const DEFAULT_FFL_PROJECT_TEMPLATE = "KANBAN";
const DEFAULT_FFL_PROJECT_KEY = "PROJ";
const DEFAULT_STATUS_NAMES = ["To Do", "In Progress", "Done"];
const DEFAULT_END_DATE_DAYS_FROM_NOW = 30;

const setProjectCreateTaskIdQueryParam = (taskId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("taskId", taskId);
    window.history.replaceState({}, "", url.toString());
};

const getElapsedDaysFromNow = (timestamp: number): number => {
    const now = moment();
    const targetDate = moment(timestamp);
    return targetDate.diff(now, "days");
};

const generateSequentialDueDates = (
    endDate: number,
    numberOfIssues: number
): string[] => {
    const startDate = moment().startOf("day");
    const finalDate = moment(endDate).startOf("day");
    const totalDays = finalDate.diff(startDate, "days");
    const intervalSizeInDays = totalDays / numberOfIssues;

    return Array.from({ length: numberOfIssues }, (_, index) => {
        const intervalStartDay = Math.floor(index * intervalSizeInDays);
        const intervalEndDay = Math.floor((index + 1) * intervalSizeInDays);
        const randomDay =
            intervalStartDay +
            Math.floor(
                Math.random() * Math.max(1, intervalEndDay - intervalStartDay)
            );

        return startDate.clone().add(randomDay, "days").format("YYYY-MM-DD");
    });
};

const mapFflResponseToSampleIssues = (
    response: JiraFFLContentGenerationResponse,
    dueDate: number
): WizardCreateProjectSampleIssue[] => {
    const { childWorkItems } = response.sampleIssues;

    const relativeDays = getElapsedDaysFromNow(dueDate);

    const issueDueDates = generateSequentialDueDates(
        dueDate,
        childWorkItems.length
    );
    const issueDueDateRelativeDays = issueDueDates.map((issueDueDate) =>
        getElapsedDaysFromNow(new Date(issueDueDate).getTime())
    );

    const childrenIssues: WizardCreateProjectSampleIssue[] = childWorkItems.map(
        (issue: MadlibContentGenerationChildWorkItem, index: number) => ({
            refId: `task-${index + 1}`,
            parentRefId: undefined,
            issueType: "Task",
            summary: issue.summary,
            description: "",
            transitionId: Transition.ToDo,
            relativeDays: issueDueDateRelativeDays[index] ?? relativeDays,
            priorityId: Priority.Medium,
            assigneeId: undefined
        })
    );

    return childrenIssues;
};

export type UseCreateJiraFflProjectParams = {
    readonly enabled: boolean;
    readonly cgsKey?: string;
    readonly onTaskIdCreated?: (taskId: string) => void;
};

/**
 * For Jira FFL provisioning flows: once site provisioning completes, fetch the cached FFL
 * content (via cgsKey), create the onboarding project, and poll until completion.
 *
 * This hook exposes an imperative `createJiraProject` function (matching the TWC pattern)
 * and a `hasCreatedProject` boolean that callers can use to gate redirect/progress flows.
 */
export const useCreateJiraFflProject = ({
    enabled,
    cgsKey,
    onTaskIdCreated
}: UseCreateJiraFflProjectParams) => {
    const stargateClient = useBxpWebStargateClient();
    const { cloudId } = useCloudStatus();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const isCreateRequestInProgress = useRef(false);
    const [hasCreatedProject, setHasCreatedProject] = useState(false);

    const createJiraProject = useCallback(async () => {
        if (
            !enabled ||
            !cloudId ||
            !cgsKey ||
            hasCreatedProject ||
            isCreateRequestInProgress.current
        ) {
            return;
        }

        try {
            isCreateRequestInProgress.current = true;

            createAnalyticsEvent({
                eventType: "track",
                action: "started",
                actionSubject: "jiraFflProjectCreation",
                actionSubjectId: "jiraAnimatedProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    experiment: "cfind4666JiraFfl",
                    cgsKey
                }
            }).fire();

            let response: JiraFFLContentGenerationResponse;
            if (cgsKey === "default") {
                response = fallbackCgsData;
            } else {
                response = await stargateClient.getJiraFFLContentByCgsKey(
                    cgsKey
                );
            }

            const dueDate =
                Date.now() +
                DEFAULT_END_DATE_DAYS_FROM_NOW * 24 * 60 * 60 * 1000;

            const sampleIssues = mapFflResponseToSampleIssues(
                response,
                dueDate
            );

            const projectName = response.sampleIssues.spaceName || "Project";

            const { taskId } = await stargateClient.postProjectCreate({
                projectName,
                cloudId,
                template: DEFAULT_FFL_PROJECT_TEMPLATE,
                issueTypes: confiugredIssueTypes,
                statusNames: DEFAULT_STATUS_NAMES,
                customDetail: {
                    enableExtraFields: true,
                    sampleDataVersion: "MANUAL"
                },
                projectKey: DEFAULT_FFL_PROJECT_KEY,
                sampleIssues
            });

            setProjectCreateTaskIdQueryParam(taskId);

            // Notify caller of taskId creation for tracking purposes
            if (onTaskIdCreated) {
                onTaskIdCreated(taskId);
            }

            const { status } = await stargateClient.pollTaskUntilComplete(
                taskId,
                cloudId,
                PROJECT_CREATE_TASK_POLLING_TIMEOUT
            );

            if (status !== "COMPLETE") {
                throw new Error("FFL project creation did not complete");
            }

            createAnalyticsEvent({
                eventType: "track",
                action: "succeeded",
                actionSubject: "jiraFflProjectCreation",
                actionSubjectId: "jiraAnimatedProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    experiment: "cfind4666JiraFfl",
                    cgsKey,
                    template: DEFAULT_FFL_PROJECT_TEMPLATE
                }
            }).fire();

            setHasCreatedProject(true);
        } catch (error) {
            createAnalyticsEvent({
                eventType: "track",
                action: "failed",
                actionSubject: "jiraFflProjectCreation",
                actionSubjectId: "jiraAnimatedProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    experiment: "cfind4666JiraFfl",
                    cgsKey,
                    template: DEFAULT_FFL_PROJECT_TEMPLATE,
                    error:
                        error instanceof Error ? error.message : "Unknown error"
                }
            }).fire();

            // Still mark as created to prevent retries and unblock flows that are gated on completion.
            setHasCreatedProject(true);
        } finally {
            isCreateRequestInProgress.current = false;
        }
    }, [
        enabled,
        cloudId,
        cgsKey,
        hasCreatedProject,
        stargateClient,
        createAnalyticsEvent,
        onTaskIdCreated
    ]);

    return { hasCreatedProject, createJiraProject };
};
